/**
 * StatCards — summary stat cards for the main dashboard.
 *
 * 3 cards; Grandstream campuses get 2 more (docs/TIME_TO_CONNECT_PLAN.md, phase 3):
 *   Avg Time to Connect — alerts with an answered intercom call only (sp_getSummaryStat v3), N/A when none
 *   Intercom Usage      — alerts with a placed intercom call / all alerts in the same 28-day event set
 *
 * Avg Response Rate and Avg Time to Connect carry the Alert Details "Based on N of M alerts"
 * note (CoverageNote, user 2026-10-22), from sp_getSummaryStat v4's count columns. M is every
 * alert in the 28 days, System Outages and alerts with no response or one over 60 minutes
 * included; the averages leave those out. Before v4 the counts are absent and no note shows.
 */

import HeaderInfoTip from '../../../../components/v2/HeaderInfoTip.jsx';
import CoverageNote, { countOf } from '../../../reports/alert-details/components/CoverageNote.jsx';
import { TIME_TO_CONNECT_INFO, INTERCOM_USAGE_INFO, TIME_TO_CONNECT_NA }
    from '../../../reports/alert-details/timeToConnect.js';

/* The averages' set: acknowledged within 60 minutes, System Outages out. */
const unresponded = (total, responded, outages) => Math.max(0, total - responded - outages);
const UNRESPONDED = 'with no response recorded, or a response over 60 minutes. The dashboard averages leave these out.';

const describeResponse = ({ measured, total, outages }) => ({
    title: 'Avg Response Rate',
    label: 'How Avg Response Rate is calculated',
    body: `The average of the ${countOf(measured, 'alert')} responded to within 60 minutes `
        + '(alert to device cleared) in the last 28 days.',
    items: [
        outages && `${countOf(outages, 'System Outage')}: the time a server was offline, not a caregiver response.`,
        unresponded(total, measured, outages) && `${countOf(unresponded(total, measured, outages), 'alert')} ${UNRESPONDED}`,
    ].filter(Boolean),
});

/* Time to Connect averages a subset of the response set, so its exclusions add one line. */
const describeConnect = (responded) => ({ measured, total, outages }) => ({
    title: 'Avg Time to Connect',
    label: 'How Avg Time to Connect is calculated',
    body: `The average of the ${countOf(measured, 'alert')} with an answered intercom call, `
        + 'from the alert to when the call was answered, in the last 28 days.',
    items: [
        outages && `${countOf(outages, 'System Outage')}: a server alert, so there is no intercom call.`,
        unresponded(total, responded, outages) && `${countOf(unresponded(total, responded, outages), 'alert')} ${UNRESPONDED}`,
        Math.max(0, responded - measured) && `${countOf(Math.max(0, responded - measured), 'alert')} with no answered intercom call.`,
    ].filter(Boolean),
});

const StatCards = ({ statData, loading, isGrandstream = false }) => {
    const count = isGrandstream ? 5 : 3;
    const rowClass = `md-stat-row${isGrandstream ? ' md-stat-row--5' : ''}`;

    if (loading) {
        return (
            <div className={rowClass}>
                {Array.from({ length: count }, (_, i) => (
                    <div key={i} className="md-stat-card">
                        <div className="md-stat-card__value" style={{ color: 'var(--color-text-muted)' }}>—</div>
                        <div className="md-stat-card__label">Loading...</div>
                    </div>
                ))}
            </div>
        );
    }

    /* The proc returns NULL for both when the campus isn't Grandstream, and for the
       average when no alert in the window used the intercom: N/A, never 0. */
    const connect = statData?.avg_time_to_connect;
    const usage = statData?.intercom_usage_pct;
    /* CoverageNote's shape. Null-safe: a v3 proc has no alerts_total, so no note. */
    const coverage = statData?.alerts_total == null ? null : {
        total: statData.alerts_total,
        outages: statData.outage_alerts,
        response_measured: statData.response_measured,
        connect_measured: statData.connect_measured,
    };
    const responded = Number(statData?.response_measured) || 0;

    return (
        <div className={rowClass}>
            <div className="md-stat-card">
                <div className="md-stat-card__value">{statData?.avg_daily_events ?? 0}</div>
                <div className="md-stat-card__label">Avg Daily Alarms</div>
            </div>
            <div className="md-stat-card">
                <div className="md-stat-card__value">{statData?.avg_response_time ?? '0.0'} min</div>
                <div className="md-stat-card__label">Avg Response Rate</div>
                <CoverageNote coverage={coverage} metric="response" describe={describeResponse} />
            </div>
            <div className="md-stat-card">
                <div className="md-stat-card__value">{statData?.percent_events_over_20 ?? '0.0'}%</div>
                <div className="md-stat-card__label">Alarms Over 20 Min</div>
            </div>
            {isGrandstream ? (
                <>
                    <div className="md-stat-card">
                        <div className="md-stat-card__value">{connect != null ? `${connect} min` : TIME_TO_CONNECT_NA}</div>
                        <div className="md-stat-card__label">
                            Avg Time to Connect
                            <HeaderInfoTip title="Time to Connect" body={TIME_TO_CONNECT_INFO} />
                        </div>
                        <CoverageNote coverage={coverage} metric="connect" describe={describeConnect(responded)} />
                    </div>
                    <div className="md-stat-card">
                        <div className="md-stat-card__value">{usage != null ? `${usage}%` : '-'}</div>
                        <div className="md-stat-card__label">
                            Intercom Usage
                            <HeaderInfoTip title="Intercom Usage" body={INTERCOM_USAGE_INFO} />
                        </div>
                    </div>
                </>
            ) : null}
        </div>
    );
};

export default StatCards;
